import { useState, useEffect, useCallback } from "react";
import { apiGet, ApiResponse, ApiError } from "../../services/api.service";

interface Payslip {
	payroll_id: number;
	extra_id: number;
	month: number;
	report_id: number;
	created_at: string;
	updated_at: string;
}

interface UseMyPayslipsReturn {
	payslips: Payslip[];
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
}

/**
 * Fetch payslips for a given extra_id
 */
export function useMyPayslips(extraId?: number): UseMyPayslipsReturn {
	const [payslips, setPayslips] = useState<Payslip[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const fetchPayslips = useCallback(async () => {
		if (!extraId) {
			setPayslips([]);
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const response: ApiResponse<Payslip[]> = await apiGet<Payslip[]>(
				`/payroll/user/${extraId}`,
				undefined,
				{
					notification: {
						enabled: false,
					},
				}
			);

			if (response.success && response.data) {
				setPayslips(response.data);
			} else {
				setError(response.error || "Fehler beim Abrufen der Lohnabrechnungen");
				setPayslips([]);
			}
		} catch (err) {
			if (err instanceof ApiError) {
				setError(err.message);
			} else if (err instanceof Error) {
				setError(err.message);
			} else {
				setError("Ein unerwarteter Fehler ist beim Abrufen der Lohnabrechnungen aufgetreten");
			}
			setPayslips([]);
		} finally {
			setLoading(false);
		}
	}, [extraId]);

	useEffect(() => {
		fetchPayslips();
	}, [fetchPayslips]);

	return {
		payslips,
		loading,
		error,
		refetch: fetchPayslips,
	};
}
